/**
 * 추이 그래프의 구간 → 요청 검색 조건 (23-A회차, UI 피드백 17).
 *
 * "10시 5분에 뭐가 있었나"는 그림만 보고는 알 수 없다. 구간을 누르면 그 구간의 시작·끝을
 * 기간으로 채운 요청 검색으로 간다. 오류 계열을 눌렀으면 상태=오류까지 건다.
 *
 * 순수 함수라 시험할 수 있다. 그리는 쪽은 결과를 주소에 넣기만 한다.
 *
 * **경계 규칙 두 가지** (23-A회차 리뷰 4).
 *   - 구간은 반열린 `[시작, 시작+폭)`이다. 검색 API의 `to`는 포함이라 `끝 - 1ms`를 준다.
 *     그러지 않으면 다음 구간의 시작 시각에 딱 맞춰 시작한 요청이 두 구간에 다 잡힌다
 *   - 원 조회 기간(`bounds`)으로 **양 끝을 자른다**. 통계는 `from <= startedAt <= to`만 세는데
 *     첫·마지막 구간은 그 밖까지 걸쳐 있어, 자르지 않으면 그림에 없던 요청이 목록에 나온다
 *
 * 기간이 초 단위까지 보존되는 것은 `time.ts`의 `toLocalInput`(초 포함)과 `SearchPanel`의
 * 첫 조회(주소의 epoch 그대로)가 맡는다.
 */
export interface SearchExtra {
  /** 오류만 볼지 */
  errorsOnly?: boolean;
  /** 요청 경로. 통계·추이가 동등 비교라 검색에도 `entryExact`로 넘긴다 */
  entry?: string;
  txType?: string;
  service?: string;
  /** 인스턴스 조건 (30회차). 통계 화면의 인스턴스 조건이 검색까지 따라간다 */
  instance?: string;
  includeSse?: boolean;
}

/** 원 조회 기간. 구간을 이 밖으로 넘기지 않는다 */
export interface RangeBounds {
  from?: number;
  to?: number;
}

function withExtra(params: URLSearchParams, extra: SearchExtra): string {
  if (extra.errorsOnly === true) params.set('status', 'error');
  // 통계·추이의 진입점은 동등 비교다. 검색의 `entry`(부분일치)로 넘기면 하위 경로가 섞인다
  if (extra.entry !== undefined && extra.entry !== '') params.set('entryExact', extra.entry);
  if (extra.txType !== undefined && extra.txType !== '') params.set('txType', extra.txType);
  if (extra.service !== undefined && extra.service !== '') params.set('service', extra.service);
  if (extra.instance !== undefined && extra.instance !== '') params.set('instance', extra.instance);
  if (extra.includeSse === true) params.set('includeSse', '1');
  return params.toString();
}

/** 구간 하나 → 검색 조건. 원 조회 기간 밖은 잘라 낸다 */
export function bucketSearchQuery(
  bucketStart: number,
  bucketMs: number,
  extra: SearchExtra = {},
  bounds: RangeBounds = {},
): string {
  const from = Math.max(bucketStart, bounds.from ?? Number.NEGATIVE_INFINITY);
  // 반열린 구간이라 마지막 1ms를 뺀다. 원 기간의 끝(포함)보다 뒤로는 못 간다
  const to = Math.min(bucketStart + bucketMs - 1, bounds.to ?? Number.POSITIVE_INFINITY);
  const params = new URLSearchParams({ from: String(from), to: String(Math.max(from, to)) });
  return withExtra(params, extra);
}

/** 기간 전체를 그대로 여는 조건. 캔버스를 못 누르는 사람이 쓰는 길이다 */
export function rangeSearchQuery(range: RangeBounds, extra: SearchExtra = {}): string {
  const params = new URLSearchParams();
  if (range.from !== undefined) params.set('from', String(range.from));
  if (range.to !== undefined) params.set('to', String(range.to));
  return withExtra(params, extra);
}
